import { LogLeadTemp } from '../interfaces/LogLeadTemp'

interface FileInfo {
  file: File
  path: string
}

/**
 * Extract all File objects from a data structure and their paths
 */
export const extractFiles = (obj: any, basePath: string = ''): FileInfo[] => {
  const files: FileInfo[] = []

  if (obj instanceof File) {
    files.push({ file: obj, path: basePath })
    return files
  }

  if (Array.isArray(obj)) {
    obj.forEach((item, index) => {
      const path = basePath ? `${basePath}[${index}]` : `[${index}]`
      files.push(...extractFiles(item, path))
    })
  } else if (obj && typeof obj === 'object' && obj.constructor === Object) {
    Object.keys(obj).forEach(key => {
      const path = basePath ? `${basePath}.${key}` : key
      files.push(...extractFiles(obj[key], path))
    })
  }

  return files
}

/**
 * Calculate total size of all files in a data chunk
 */
export const calculateChunkSize = (data: LogLeadTemp[]): number => {
  let totalSize = 0

  data.forEach(item => {
    const files = extractFiles(item)
    files.forEach(fileInfo => {
      totalSize += fileInfo.file.size
    })
  })

  return totalSize
}

/**
 * Convert data to FormData with dynamic keys (RECOMMENDED APPROACH)
 * This approach directly maps files to their structure paths without placeholders
 */
export const convertToFormDataDynamic = (data: LogLeadTemp[]): FormData => {
  const formData = new FormData()

  const newData = data.map(({ pipelineName, subProgressName, progressName, leadProgressColor, ...rest }) => rest)

  // Process each data item with dynamic structure
  newData.forEach((item, dataIndex) => {
    // Add all non-file properties directly
    addToFormDataRecursive(formData, item, `data[${dataIndex}]`)
  })

  return formData
}

/**
 * Recursively add object properties to FormData with dynamic keys
 */
const addToFormDataRecursive = (formData: FormData, obj: any, basePath: string): void => {
  if (obj instanceof File) {
    // Direct file upload with dynamic path
    formData.append(basePath, obj)
    return
  }

  if (obj === null || obj === undefined) {
    formData.append(basePath, '')
    return
  }

  if (Array.isArray(obj)) {
    obj.forEach((item, index) => {
      addToFormDataRecursive(formData, item, `${basePath}[${index}]`)
    })
    return
  }

  if (typeof obj === 'object' && obj.constructor === Object) {
    Object.keys(obj).forEach(key => {
      addToFormDataRecursive(formData, obj[key], `${basePath}[${key}]`)
    })
    return
  }

  // Primitive values (string, number, boolean)
  formData.append(basePath, String(obj))
}

/**
 * Split data into chunks based on size limit (default 10MB)
 */
export const chunkDataBySize = (data: LogLeadTemp[], maxSizeBytes: number = 20 * 1024 * 1024): LogLeadTemp[][] => {
  const chunks: LogLeadTemp[][] = []
  let currentChunk: LogLeadTemp[] = []
  let currentSize = 0

  data.forEach(item => {
    const itemFiles = extractFiles(item)
    const itemSize = itemFiles.reduce((sum, fileInfo) => sum + fileInfo.file.size, 0)

    // If adding this item would exceed the limit, start a new chunk
    if (currentSize + itemSize > maxSizeBytes && currentChunk.length > 0) {
      chunks.push([...currentChunk])
      currentChunk = [item]
      currentSize = itemSize
    } else {
      currentChunk.push(item)
      currentSize += itemSize
    }
  })

  // Add the last chunk if it has data
  if (currentChunk.length > 0) {
    chunks.push(currentChunk)
  }

  return chunks
}

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B'

  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
} 